import { ApiCustomer } from '@vend/types'
import React from 'react'

import { ApolloEditCustomerModal } from '@vend/business-components/dist/components/EditCustomerModal'
import { Customer } from '@vend/business-components/dist/components/EditCustomerModal/common/types/Customer'
import { getDisplayNameFromGqlCustomer } from '@vend/react-utilities'
import ReactDOM from 'react-dom'
import { useAppDispatch, useAppSelector } from '../state/hooks'
import {
  attachCustomerToSale,
  selectSaleCustomer,
} from '../state/slices/currentSale/currentSaleSlice'

import { wrToastSuccess } from '../components/react/helios/ToastNotifications/WrToastNotifications'
import { EVENT, PHONE_REGEX } from '../constants'
import { useCustomerManager } from '../ng-compat/hooks'
import { $rootScope } from '../ng-compat/imports'
import { createApiCustomer, isApiCustomer } from '../util/api-customer-utils'
import { isValidCustomer } from '../functions/sale'

type Props = {
  onClose: (event?: React.MouseEvent | KeyboardEvent) => void
  customerSearchQuery?: string
}

/**
 * Need to transform customer for type compatibility between sale customer & EditCustomerModal customer
 * To update existing customer, passing in customer id is enough
 */
const transformCustomer = (customer: ApiCustomer) => {
  return {
    id: customer.id,
  }
}

/**
 * Populate customer will create a customer object based on the query string
 */
const populateCustomer = (query?: string) => {
  if (!query) {
    return undefined
  }

  if (query.includes('@')) {
    return { contact: { email: query } }
  }

  if (query.match(PHONE_REGEX)) {
    return { contact: { mobile: query } }
  }

  const [firstName, ...rest] = query.split(' ')

  return {
    firstName: (firstName || '').trim(),
    lastName: (rest ? rest.join(' ') : '').trim(),
  }
}

/**
 * This component is to update an existing sale customer
 */
export const UpdateSaleCustomerModal = (props: Props) => {
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const customerManager = useCustomerManager()
  const dispatch = useAppDispatch()
  const { onClose, customerSearchQuery } = props
  const customer =
    // INC-1901: Check the customer isn't WALKIN so we don't try to edit that customer
    saleCustomer && isValidCustomer(saleCustomer)
      ? transformCustomer(saleCustomer)
      : populateCustomer(customerSearchQuery)

  const onSave = async (newCustomer: Customer, originalCustomer: Customer) => {
    const updatedCustomer = await customerManager.refreshCustomer({
      id: newCustomer.id,
    })
    dispatch(
      attachCustomerToSale(
        isApiCustomer(updatedCustomer)
          ? updatedCustomer
          : createApiCustomer(updatedCustomer)
      )
    )
    $rootScope.$emit(EVENT.SALE_CUSTOMER_UPDATED)

    const customerName = getDisplayNameFromGqlCustomer(newCustomer)
    const toastMsgKey = originalCustomer?.id
      ? 'customer.update.success'
      : 'customer.create.success'

    wrToastSuccess(toastMsgKey, { customerName })
  }

  if (!customer) return null

  return ReactDOM.createPortal(
    <ApolloEditCustomerModal
      onClose={onClose}
      customer={customer}
      onSave={onSave}
    />,
    document.body
  )
}
